'use client';

/**
 * shadcn/ui sheet: a slide-in panel built on the same Radix dialog primitive as
 * dialog.tsx (no extra dependency), styled for the calendar's event sidebar (Merkuro/Outlook
 * pattern): the page stays visible behind a dim scrim, the panel slides in from the right
 * (~200ms) and takes the full viewport width below the sm breakpoint. Radix's modal behavior
 * supplies the a11y core: role="dialog" + aria-modal, focus trapping, focus return on close,
 * Escape to dismiss and scrim-click dismissal.
 *
 * Sides follow the upstream shadcn sheet: `right` (default, unchanged for the calendar),
 * plus `top`/`bottom` for full-width surfaces - the room's mobile quick settings mounts the
 * shared panel as a bottom sheet where a floating popover was cramped and clipped.
 */
import * as React from 'react';
import type { JSX } from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/** Slide direction, inset and sizing per side (upstream shadcn sheetVariants, plain map). */
const SHEET_SIDE_CLASSES = {
  right:
    'inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
  top: 'inset-x-0 top-0 h-auto border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
  bottom:
    'inset-x-0 bottom-0 h-auto max-h-[85dvh] overflow-y-auto border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
} as const;

type SheetSide = keyof typeof SHEET_SIDE_CLASSES;

function Sheet(props: React.ComponentProps<typeof SheetPrimitive.Root>): JSX.Element {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger(props: React.ComponentProps<typeof SheetPrimitive.Trigger>): JSX.Element {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose(props: React.ComponentProps<typeof SheetPrimitive.Close>): JSX.Element {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>): JSX.Element {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      // The scrim stays translucent on purpose: unlike the room dialog (BR-DM-3, fully
      // opaque) the drawer's point is the dimmed calendar behind it.
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60',
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  closeLabel = 'Close',
  side = 'right',
  hideClose = false,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  /** Accessible name of the corner close button (i18n: the caller passes the t() string). */
  readonly closeLabel?: string;
  /** Slide-in edge; `right` keeps the calendar drawer, `bottom` hosts the mobile sheets. */
  readonly side?: SheetSide;
  /**
   * Suppresses the built-in corner close button for surfaces that carry their own (the mobile
   * quick-settings sheet reuses the shared panel's header close).
   */
  readonly hideClose?: boolean;
}): JSX.Element {
  return (
    <SheetPrimitive.Portal data-slot="sheet-portal">
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        // Radix 1.1.x dropped the aria-modal attribute in favor of hiding the outside
        // content from AT; the modal drawer still declares itself explicitly (the a11y
        // contract of the event sidebar keeps the classic modal-dialog semantics).
        aria-modal="true"
        className={cn(
          'data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-0 bg-card shadow-2xl duration-200',
          SHEET_SIDE_CLASSES[side],
          className,
        )}
        {...props}
      >
        {children}
        {hideClose ? null : (
          <SheetPrimitive.Close
            data-sheet-close
            className="absolute top-4 right-4 rounded-md p-1 text-zinc-500 opacity-70 transition-opacity hover:bg-white/10 hover:text-white focus:outline-none disabled:pointer-events-none"
            aria-label={closeLabel}
          >
            <XIcon className="size-4" />
            <span className="sr-only">{closeLabel}</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col gap-1.5 text-left', className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>): JSX.Element {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('font-semibold text-foreground', className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>): JSX.Element {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetTitle,
  SheetTrigger,
};
